import { zodResolver } from '@hookform/resolvers/zod';
import { TextField, styled } from '@mui/material';
import { BANK_RECONCILIATION } from 'adminConstants/routes';
import Button from 'components/Button';
import CurrencySelector from 'components/CurrencySelector';
import PageLoader from 'components/PageLoader';
import FullHeightView from 'components/new/FullHeightView';
import PageSection from 'components/new/PageSection';
import { useLayoutContext } from 'contexts/LayoutContext';
import { CurrencyCode } from 'further-types/lp/vehicle';
import CountrySelector from 'further-ui/components/CountrySelector';
import FormField from 'further-ui/components/FormField';
import { Column, Row } from 'further-ui/components/Layout';
import { bankReconciliation as rules } from 'further-ui/validations';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import { Control, Controller, FieldPath, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { z } from 'zod';

const formSchema = rules.AddBankAccountInput;

type FormValues = z.infer<typeof formSchema>;

type Props = {
  onConnectBankFeed?: (bankAccountId: string) => void;
  bankAccount?: {
    id: string;
    accountName: string;
    bankName: string;
    accountNumber?: string;
    sortCode?: string;
    swift?: string;
    additionalBankCode?: string;
    additionalPaymentInformation?: string;
    customPaymentReference?: string;
    beneficiaryName?: string;
    iban?: string;
    accountCurrency: CurrencyCode;
    accountAddress?: {
      address1?: string | null;
      address2?: string | null;
      city?: string | null;
      postcode?: string | null;
      country?: string | null;
    };
    bankFeedAccountId?: string;
  };
};

const StyledColumn = styled(Column)(({ theme }) => ({
  paddingBottom: theme.spacing(theme.spacingSizes.lg),
}));

const Field: React.FC<{
  label: string;
  name: FieldPath<FormValues>;
  control: Control<FormValues>;
  tooltipText?: string;
  placeholder?: string;
}> = ({ label, control, name, tooltipText, placeholder }) => (
  <FormField label={label} tooltip={tooltipText} size="md" spacing="xs">
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <TextField
          variant="outlined"
          size="small"
          fullWidth
          placeholder={placeholder}
          {...field}
          error={!!error}
          helperText={error?.message}
        />
      )}
    />
  </FormField>
);

const AccountSettings: React.FC<Props> = ({
  bankAccount,
  onConnectBankFeed,
}) => {
  const { selectedVehicleId } = useLayoutContext();
  const history = useHistory();

  const mutationHandler = useHandleTrpcMutation()({
    successMessage: bankAccount
      ? 'Bank account updated.'
      : 'Bank account added.',
    onSuccess: () => {
      if (bankAccount) return;

      history.push(BANK_RECONCILIATION);
    },
  });

  const updateBankAccount =
    api.bankAccount.updateBankAccount.useMutation(mutationHandler);

  const addBankAccount =
    api.bankAccount.addBankAccount.useMutation(mutationHandler);

  const { vehicle, isLoading } = useVehicle(selectedVehicleId);

  const { control, handleSubmit, setValue } = useForm<FormValues>({
    shouldFocusError: true,
    resolver: zodResolver(formSchema),
    values: {
      bankAccount: bankAccount
        ? {
            ...bankAccount,
            accountAddress: bankAccount.accountAddress
              ? {
                  address1: bankAccount.accountAddress.address1 ?? '',
                  address2: bankAccount.accountAddress.address2 ?? undefined,
                  city: bankAccount.accountAddress.city ?? '',
                  postcode: bankAccount.accountAddress.postcode ?? '',
                  country: bankAccount.accountAddress.country ?? '',
                }
              : undefined,
          }
        : {
            accountName: '',
            bankName: '',
            iban: '',
            accountCurrency: vehicle?.baseCurrency ?? CurrencyCode.GBP,
            accountAddress: {
              address1: '',
              city: '',
              postcode: '',
              country: '',
            },
          },
    },
    criteriaMode: 'all',
  });

  const onSubmit = (data: FormValues) => {
    if (bankAccount) {
      updateBankAccount.mutate({
        vehicleId: selectedVehicleId ?? '',
        bankAccount: {
          id: bankAccount.id,
          ...data.bankAccount,
        },
      });
    } else {
      addBankAccount.mutate({
        vehicleId: selectedVehicleId ?? '',
        ...data,
      });
    }
  };

  const handleConnectBankFeed = () => {
    onConnectBankFeed?.(bankAccount!.id);
  };

  const isPending = updateBankAccount.isPending || addBankAccount.isPending;

  if (isLoading) return <PageLoader />;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FullHeightView
        actionBar={
          <Row justify="flex-end">
            <Button
              variant="contained"
              size="large"
              type="submit"
              loading={isPending}
            >
              {bankAccount ? 'Update account' : 'Add account'}
            </Button>
          </Row>
        }
      >
        <StyledColumn spacing="lg">
          {!!bankAccount && (
            <div>
              <Button
                variant="contained"
                size="large"
                color="primary"
                disabled={!!bankAccount?.bankFeedAccountId}
                onClick={handleConnectBankFeed}
              >
                {bankAccount?.bankFeedAccountId
                  ? 'Bank feed connected'
                  : 'Connect bank feed to account'}
              </Button>
            </div>
          )}
          <PageSection spacing="lg" title="Account details">
            {' '}
            <Field
              control={control}
              name="bankAccount.bankName"
              label="Bank name"
              placeholder="Enter bank name"
            />
            <Field
              control={control}
              name="bankAccount.accountName"
              label="Account description"
              tooltipText="This should be a unique name used to refer to this account in the system."
              placeholder="Enter account description"
            />
            <FormField label="Account currency" size="md" spacing="xs">
              <Controller
                control={control}
                name="bankAccount.accountCurrency"
                render={({
                  field: { value, ...rest },
                  fieldState: { error },
                }) => (
                  <CurrencySelector
                    {...rest}
                    placeholder="Select currency"
                    error={error}
                    value={value}
                  />
                )}
              />
            </FormField>
            <Field
              control={control}
              name="bankAccount.beneficiaryName"
              label="Account holder name"
              placeholder="Enter account holder name"
            />
            <Field control={control} name="bankAccount.iban" label="IBAN" />
            <Field
              control={control}
              name="bankAccount.accountNumber"
              placeholder="Enter account number"
              label="Account number"
            />
            <Field
              control={control}
              name="bankAccount.sortCode"
              placeholder="Enter Sort/ABA/ACH code"
              label="Sort/ABA/ACH code"
            />
            <Field
              control={control}
              name="bankAccount.additionalBankCode"
              label="Additional bank code"
              placeholder="Enter additional bank code"
            />
            <Field
              control={control}
              placeholder="Enter SWIFT/BIC"
              name="bankAccount.swift"
              label="SWIFT/BIC"
            />
            <Field
              tooltipText="By default, the system will create an automated unique payment reference for each investor. If you wish to use a fixed payment reference for all investors, overriding the system reference, please input it here."
              control={control}
              name="bankAccount.customPaymentReference"
              label="Payment reference"
              placeholder="Enter payment reference"
            />
            <Field
              tooltipText="Write any payment guidance here. If no guidance is required, leave this field blank."
              control={control}
              name="bankAccount.additionalPaymentInformation"
              label="Payment information"
              placeholder="Enter payment information"
            />
          </PageSection>
          <PageSection
            spacing="lg"
            withoutBottomDivider
            title="Account address"
          >
            <Field
              control={control}
              name="bankAccount.accountAddress.address1"
              label="Address line 1"
              placeholder="Enter address line 1"
            />
            <Field
              control={control}
              name="bankAccount.accountAddress.address2"
              label="Address line 2"
              placeholder="Enter address line 2"
            />
            <Field
              control={control}
              name="bankAccount.accountAddress.city"
              label="Town/City"
              placeholder="Enter town/city"
            />
            <Field
              control={control}
              name="bankAccount.accountAddress.postcode"
              label="Postal code"
              placeholder="Enter postal code"
            />
            <FormField label="Country" size="md" spacing="xs">
              <Controller
                control={control}
                name="bankAccount.accountAddress.country"
                render={({
                  field: { value, ref, ...rest },
                  fieldState: { error },
                }) => (
                  <CountrySelector
                    {...rest}
                    ref={ref}
                    value={value}
                    name="bankAccount.accountAddress.country"
                    placeholder="Select country"
                    error={error}
                    onChange={(value) => {
                      setValue(
                        'bankAccount.accountAddress.country',
                        value ?? '',
                      );
                    }}
                  />
                )}
              />
            </FormField>
          </PageSection>
        </StyledColumn>
      </FullHeightView>
    </form>
  );
};

export default AccountSettings;
